import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

import { Tooltip, Typography } from 'antd'
import styled from 'styled-components'
import { BarChart } from '@mui/x-charts'
import { chartsTooltipClasses } from '@mui/x-charts/ChartsTooltip'
import { colours } from '@qflow/theme'

import { useUserProjectRole } from 'portal/lib/hooks'
import { icons, SmallSpinner } from 'portal/lib/primitives'
import * as routes from 'portal/routes'

import {
  Tile,
  TileNumber,
  TileRow,
  TileTitle,
  Tiles,
  TitleBar,
  TitleBarIcon
} from './commonStyles'
import {
  loadAlertsSummary,
  loadEnabledAlertRulesCount
} from 'portal/store/home/actions'

export const Alerts = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()

  const { isProjectAdmin } = useUserProjectRole(accountId, projectId)
  const { Title } = Typography

  useEffect(() => {
    if (accountId && projectId) {
      dispatch(loadAlertsSummary(accountId, projectId))
      dispatch(loadEnabledAlertRulesCount(accountId, projectId))
    }
  }, [dispatch, accountId, projectId])

  const alertsSummary = useSelector(
    (state: any) => state.home.alerts.alertsSummary
  )

  const enabledAlertRulesCount = useSelector(
    (state: any) => state.home.alerts.enabledAlertRulesCount
  )

  const createUri = (route: string) => {
    if (accountId && projectId) {
      return generatePath(route, {
        accountId,
        projectId
      })
    }
    return ''
  }

  const projectSettingsUri = createUri(routes.PROJECTSETTINGS)
  const alertsMaterialCertificationsUri = createUri(
    routes.ALERTS_MATERIAL_CERTIFICATIONS_TABLE
  )
  const alertsQualityAssuranceUri = createUri(
    routes.ALERTS_QUALITY_ASSURANCE_TABLE
  )
  const alertsSubmissionIssuesUri = createUri(
    routes.ALERTS_SUBMISSION_ISSUES_TABLE
  )
  const alertsWasteComplianceUri = createUri(
    routes.ALERTS_WASTE_COMPLIANCE_TABLE
  )

  // Data for the bar chart
  const dataForBarChart = useMemo(
    () => [
      {
        data: alertsSummary.data?.recentAlertsOverTime.materialCertifications,
        stack: 'A',
        label: 'Material Certifications',
        color: colours.CORE_NAVY
      },
      {
        data: alertsSummary.data?.recentAlertsOverTime.wasteCompliance,
        stack: 'A',
        label: 'Waste Compliance',
        color: colours.TEAL
      },
      {
        data: alertsSummary.data?.recentAlertsOverTime.qualityAssurance,
        stack: 'A',
        label: 'Quality Assurance',
        color: colours.CONSTRUCTION_ORANGE
      },
      {
        data: alertsSummary.data?.recentAlertsOverTime.submissionIssues,
        stack: 'A',
        label: 'Submission Issues',
        color: colours.YELLOW
      }
    ],
    [alertsSummary.data]
  )

  const columnLabelsForBarChart = useMemo(() => {
    const columns: string[] = []
    alertsSummary.data?.recentAlertsOverTime.timePeriods.forEach(
      (timePeriod: string) => {
        // Convert timePeriod (e.g., "2026-02-11") to a more readable format (e.g., "Feb") for the chart labels
        // Backend provides date in UTC format so we append 'Z' to ensure correct parsing
        columns.push(
          new Date(`${timePeriod}T00:00:00Z`).toLocaleString('default', {
            month: 'short'
          })
        )
      }
    )
    return columns
  }, [alertsSummary.data?.recentAlertsOverTime.timePeriods])

  return (
    <>
      <TitleBar>
        <TitleBarIcon>
          <icons.ShieldTick colour="currentColor" />
        </TitleBarIcon>
        <Title level={4}>Alerts</Title>
      </TitleBar>
      <TwoColumns>
        <Column>
          <Tile
            style={{
              height: '100%',
              paddingLeft: '0',
              paddingRight: '1rem',
              paddingTop: '0.875rem'
            }}
          >
            {alertsSummary.loading ? (
              <SpinnerWrapper>
                <SmallSpinner />
              </SpinnerWrapper>
            ) : alertsSummary.error ? (
              <SpinnerWrapper>
                <Title level={5} style={{ textAlign: 'center' }}>
                  We're struggling to fetch your data. Please try again later.
                </Title>
              </SpinnerWrapper>
            ) : (
              <BarChart
                series={dataForBarChart}
                borderRadius={10}
                grid={{ horizontal: true }}
                xAxis={[{ scaleType: 'band', data: columnLabelsForBarChart }]}
                slotProps={{
                  legend: {
                    sx: {
                      gap: 1,
                      rowGap: 1
                    }
                  },
                  tooltip: {
                    sx: {
                      [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.labelCell}`]:
                        {
                          color: colours.CORE_NAVY,
                          fontSize: '0.875rem'
                        },
                      [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.valueCell}`]:
                        {
                          fontSize: '0.875rem'
                        },
                      [`&.${chartsTooltipClasses.root}`]: {
                        borderRadius: '0.625rem'
                      },
                      [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.mark}`]:
                        {
                          borderRadius: '0.25rem',
                          marginTop: '-1px'
                        },
                      [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.paper}`]:
                        {
                          paddingTop: '0.375rem'
                        }
                    }
                  }
                }}
                sx={{
                  'height': '100%',
                  'maxHeight': '232px',
                  '& .MuiChartsLegend-mark': {
                    borderRadius: '0.25rem !important',
                    marginTop: '-1px'
                  }
                }}
                margin={{ left: 0, right: 0, top: 16, bottom: 0 }}
              />
            )}
          </Tile>
        </Column>
        <Column>
          <Tiles
            style={{
              gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
              gridTemplateAreas:
                '"one four four four four" "five five five five five"',
              gap: '0.75rem'
            }}
          >
            <Tile
              style={{ gridArea: 'one' }}
              {...(isProjectAdmin
                ? {
                    onClick: () => {
                      navigate(`${projectSettingsUri}?view=alertSettings`)
                    }
                  }
                : {})}
            >
              <Tooltip title="Number of Alerts enabled">
                <TileRow>
                  <Title level={5}>Active</Title>
                </TileRow>
              </Tooltip>
              <TileRow>
                {enabledAlertRulesCount.loading ? (
                  <SmallSpinner />
                ) : (
                  <TileNumber>{enabledAlertRulesCount?.count}</TileNumber>
                )}
              </TileRow>
            </Tile>

            <Tile style={{ gridArea: 'four', minHeight: '7.25rem' }}>
              <TileRow>
                <Title level={5}>Most frequent alerts</Title>
              </TileRow>
              <TileRow>
                {alertsSummary.loading ? (
                  <SmallSpinner />
                ) : alertsSummary?.data?.mostFrequent ? (
                  <ul>
                    {alertsSummary.data.mostFrequent.map((alert, index) => (
                      <li key={index}>{alert}</li>
                    ))}
                  </ul>
                ) : (
                  'No data available'
                )}
              </TileRow>
            </Tile>
            <Tile>
              <TileRow>
                <Title level={5}>Open</Title>
              </TileRow>
              <TileRow>
                <TileRow>
                  {alertsSummary.loading ? (
                    <SmallSpinner />
                  ) : (
                    <TileNumber>
                      {new Intl.NumberFormat().format(
                        alertsSummary?.data?.activeTotal
                      )}
                    </TileNumber>
                  )}
                </TileRow>
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                navigate(alertsMaterialCertificationsUri)
              }}
            >
              <TileRow>
                <TileTitle>Material Certifications</TileTitle>
              </TileRow>
              <TileRow>
                {alertsSummary.loading ? (
                  <SmallSpinner />
                ) : (
                  <TileNumber>
                    {new Intl.NumberFormat().format(
                      alertsSummary?.data?.activeMaterialCertifications
                    )}
                  </TileNumber>
                )}
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                navigate(alertsWasteComplianceUri)
              }}
            >
              <TileRow>
                <TileTitle>Waste Compliance</TileTitle>
              </TileRow>
              <TileRow>
                {alertsSummary.loading ? (
                  <SmallSpinner />
                ) : (
                  <TileNumber>
                    {new Intl.NumberFormat().format(
                      alertsSummary?.data?.activeWasteCompliance
                    )}
                  </TileNumber>
                )}
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                navigate(alertsQualityAssuranceUri)
              }}
            >
              <TileRow>
                <TileTitle>Quality Assurance</TileTitle>
              </TileRow>
              <TileRow>
                {alertsSummary.loading ? (
                  <SmallSpinner />
                ) : (
                  <TileNumber>
                    {new Intl.NumberFormat().format(
                      alertsSummary?.data?.activeQualityAssurance
                    )}
                  </TileNumber>
                )}
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                navigate(alertsSubmissionIssuesUri)
              }}
            >
              <TileRow>
                <TileTitle>Submission Issues</TileTitle>
              </TileRow>
              <TileRow>
                {alertsSummary.loading ? (
                  <SmallSpinner />
                ) : (
                  <TileNumber>
                    {new Intl.NumberFormat().format(
                      alertsSummary?.data?.activeSubmissionIssues
                    )}
                  </TileNumber>
                )}
              </TileRow>
            </Tile>
          </Tiles>
        </Column>
      </TwoColumns>
    </>
  )
}

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr calc(50% + 0.5rem);
  gap: 1rem;
`
const Column = styled.div`
  align-self: stretch;
  justify-self: stretch;
`
const SpinnerWrapper = styled.div`
  margin-top: 5rem;
`
